import type { ComponentPropsWithoutRef, Ref } from 'react';
import { cn } from '@/lib/cn';

/**
 * The translucent list column (`FE-04.10`; `docs/design/stage2/entry-surfaces.md`
 * and `finding-things.md` both consume it, listed-not-specified). Its full
 * shape is `FE-11.1`'s — this ships the primitive that shape needs.
 *
 * `styles.css`'s `--color-panel` is measured to keep `--color-ink` at or above
 * 4.5:1 over ANY board colour (the 92% floor). `--color-ink-muted` needs 98%
 * and is therefore NOT guaranteed on it — so `ListPanel` itself renders only
 * `text-ink` (the safe default for its children) and never a muted variant.
 *
 * The constraint this file encodes structurally, not just in a comment:
 * `ListPanelInset` is the ONLY place inside a panel where muted text is legal.
 * It is an opaque `--color-surface-raised` island — a caller reaching for
 * `text-ink-muted` inside a bare `ListPanel` has no primitive that makes that
 * safe; reaching for `ListPanelInset` does.
 */
export interface ListPanelProps extends ComponentPropsWithoutRef<'div'> {
  ref?: Ref<HTMLDivElement>;
}

export function ListPanel({ className, children, ref, ...rest }: ListPanelProps) {
  return (
    <div
      ref={ref}
      className={cn('rounded-card bg-panel p-2 text-ink', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface ListPanelInsetProps extends ComponentPropsWithoutRef<'div'> {
  ref?: Ref<HTMLDivElement>;
}

/**
 * The opaque inset a `ListPanel` must use for muted text — see the file
 * comment. `bg-surface-raised` clears the 98% floor `--color-ink-muted` needs
 * regardless of the board colour behind the panel.
 */
export function ListPanelInset({
  className,
  children,
  ref,
  ...rest
}: ListPanelInsetProps) {
  return (
    <div
      ref={ref}
      className={cn('rounded-card bg-surface-raised p-2', className)}
      {...rest}
    >
      {children}
    </div>
  );
}
